import { ChangeEvent } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import ReactInputMask from 'react-input-mask'
import { TextField as MUITextField } from '@mui/material'

import { DefaultElementProps } from 'common/interfaces'
import InputLabel from 'components/InputLabel'
import { SizingContainer } from 'components/SizingContainer'
import addMaskRules from 'utils/addMaskRules'
import { useTemplate, useToggle } from 'utils/hook'
import validationsToRules from 'utils/validationsToRules'

import { TextField as TextFieldProps } from './interface'
import PasswordEndAdornment from './PasswordEndAdornment'

export default function TextField({
  name,
  size,
  label,
  mask,
  onFocus,
  onChange,
  placeholder,
  validations,
  defaultValue,
  hidden = false,
  silent = false,
  disabled = false,
}: TextFieldProps & DefaultElementProps): JSX.Element {
  const render = useTemplate()
  const [visible, setVisible] = useToggle(false)

  const { control } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: render(defaultValue ?? ''),
    rules: addMaskRules(validationsToRules(validations), mask),
  })

  const fieldProps = {
    ...field,
    onFocus,
    inputRef: field.ref,
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      field.onChange(e)
      onChange?.({ value: e.target.value })
    },
  } as const

  let textField = (
    <MUITextField
      required={validations.required?.value}
      fullWidth
      type={hidden && !visible ? 'password' : 'text'}
      error={error !== undefined}
      helperText={error?.message}
      placeholder={render(placeholder ?? '')}
      label={<InputLabel label={render(label)} />}
      InputProps={{
        endAdornment: hidden ? (
          <PasswordEndAdornment visible={visible} onClick={setVisible} />
        ) : undefined,
      }}
      style={silent ? { display: 'none' } : {}}
      // disabled={disabled}
      {...(mask === undefined ? fieldProps : {})}
    />
  )

  if (mask !== undefined) {
    textField = (
      <ReactInputMask mask={mask.value} {...fieldProps}>
        {textField}
      </ReactInputMask>
    )
  }

  return (
    <SizingContainer className="text-field-wrap" size={size}>
      {textField}
    </SizingContainer>
  )
}
